import type { PixelEventName } from '@xshop/shared';
import { injectScript, type BrowserPixel, type PixelPayload } from './loader';

const SCRIPT_SRC = 'https://sc-static.net/scevent.min.js';
const SCRIPT_ID = 'xshop-snapchat-pixel';

// Snapchat's own event names for the five funnel steps.
const SNAP_EVENTS: Record<PixelEventName, string> = {
  PageView: 'PAGE_VIEW',
  ViewContent: 'VIEW_CONTENT',
  AddToCart: 'ADD_CART',
  InitiateCheckout: 'START_CHECKOUT',
  Purchase: 'PURCHASE'
};

// The queueing stub Snapchat's snippet installs so calls before the SDK loads are not lost.
interface Snaptr {
  (...args: unknown[]): void;
  handleRequest?: (...args: unknown[]) => void;
  queue: unknown[][];
}

type SnaptrWindow = Window & { snaptr?: Snaptr };

// Installs the snaptr stub unless another tag on the page already provided one.
function ensureSnaptr(): Snaptr {
  const target = window as SnaptrWindow;
  if (target.snaptr) return target.snaptr;

  const snaptr = ((...args: unknown[]) => {
    if (snaptr.handleRequest) snaptr.handleRequest.apply(snaptr, args);
    else snaptr.queue.push(args);
  }) as Snaptr;
  snaptr.queue = [];
  target.snaptr = snaptr;
  injectScript(SCRIPT_SRC, SCRIPT_ID);
  return snaptr;
}

// Builds the properties Snapchat reads off a value-bearing event.
function snapProperties(payload: PixelPayload): Record<string, unknown> {
  const properties: Record<string, unknown> = { client_dedup_id: payload.eventId };
  if (payload.currency) properties.currency = payload.currency;
  if (payload.value !== undefined) properties.price = payload.value;
  if (payload.contentId) properties.item_ids = [payload.contentId];
  if (payload.orderId) properties.transaction_id = payload.orderId;
  return properties;
}

// The Snapchat browser pixel. Browser-only today — we send nothing to its Conversions API.
export const snapchatPixel: BrowserPixel = {
  // Initialises one Snap pixel per configured id.
  install(ids) {
    const snaptr = ensureSnaptr();
    for (const id of ids) snaptr('init', id);
  },

  // Fires one event, carrying our event id as Snapchat's client deduplication key.
  track(event, payload) {
    (window as SnaptrWindow).snaptr?.('track', SNAP_EVENTS[event], snapProperties(payload));
  }
};
